// ⚠ SIMULATED DATA — OPT-IN, SUPER ADMIN ONLY, SCREEN ONLY.
//
// Fakes riders, therapists, bookings and alerts IN THE BROWSER so the live map
// can be reviewed with something on it: /live-map?demo=1. Nothing is written
// anywhere; no real record is touched.
//
// Leon's call (2026-09-29): available on the production admin so Bam can
// review the map on a real link, not a screenshot. Two locks remain:
//   1. `?demo=1` must be in the URL — never on by default;
//   2. in production, only a Super Admin gets it — a hub manager handed the
//      same link sees the real map.
// This page once shipped a fake "demo journey" switched on by default
// (OQ-99), which made every moving thing on the map a lie — hence the locks
// and the on-screen badge. Never make it default-on, and never widen the role.

import { useEffect, useMemo, useState } from "react";
import type { OpsLiveTrip, OpsLiveTripTrack, StaffOpsStatusWire } from "@bednbody/types";
import type { LiveMapBooking } from "./live-map.hooks";
import type { ClockedInStaffMapItem, HubMapItem } from "./live-map.shared";

/**
 * On only when asked for in the URL (`?demo=1`), and in production only for a
 * Super Admin — so a hub manager handed the same link still sees the real map
 * and cannot mistake fake riders for a real shift.
 */
export function isLiveMapDemo(isSuperAdmin: boolean): boolean {
  const asked = new URLSearchParams(window.location.search).has("demo");
  return asked && (import.meta.env.DEV || isSuperAdmin);
}

type Point = { lat: number; lng: number };

/** Fallback when no hub is mapped: Cainta. */
const CAINTA: Point = { lat: 14.5786, lng: 121.1222 };

function offset(origin: Point, northKm: number, eastKm: number): Point {
  return {
    lat: origin.lat + northKm / 110.574,
    lng: origin.lng + eastKm / (111.32 * Math.cos((origin.lat * Math.PI) / 180)),
  };
}

function minutesAgo(minutes: number, now: number): string {
  return new Date(now - minutes * 60_000).toISOString();
}

/** Deterministic fake uuid, so selections survive re-renders. */
function id(n: number): string {
  return `00000000-0000-4000-8000-${String(n).padStart(12, "0")}`;
}

function along(path: readonly Point[], t: number): Point {
  const segments = path.length - 1;
  const clamped = Math.min(Math.max(t, 0), 0.9999) * segments;
  const i = Math.floor(clamped);
  const f = clamped - i;
  const a = path[i]!;
  const b = path[i + 1]!;
  return { lat: a.lat + (b.lat - a.lat) * f, lng: a.lng + (b.lng - a.lng) * f };
}

function status(
  over: Partial<StaffOpsStatusWire> & Pick<StaffOpsStatusWire, "activity" | "label">,
): StaffOpsStatusWire {
  return {
    connected: true,
    since: null,
    lastContactAt: new Date().toISOString(),
    bookingId: null,
    bookingRef: null,
    pauseKind: null,
    pickUpFor: null,
    ...over,
  };
}

export interface LiveMapDemo {
  staff: ClockedInStaffMapItem[];
  trips: OpsLiveTrip[];
  bookings: LiveMapBooking[];
  noShows: {
    id: string;
    party: string;
    created_at: string;
    booking_id: string;
    bookings: { ref: string; hub_id: string };
  }[];
  pendingCash: { id: string; amount: number; status: string; hub_id: string; created_at: string }[];
  trackFor: (tripId: string | null) => OpsLiveTripTrack | undefined;
}

/**
 * Real staff WITH a photo, added to the simulator as Active pins so faces can be
 * reviewed on the map (Leon, 2026-10-01). Only their id, name and role — the
 * photo is still signed under storage RLS like anywhere else. Their position
 * and status here are invented, like every other pin in this mode.
 */
export interface DemoRealStaff {
  id: string;
  name: string;
  role: "therapist" | "rider";
}

/** Stable when there are none, so the simulator does not rebuild on every render. */
const NO_REAL_STAFF: readonly DemoRealStaff[] = [];

/** Where the real faces stand, km north/east of the hub — clear of the fakes. */
const REAL_STAFF_SPOTS: readonly [number, number][] = [
  [0.9, -1.5],
  [-1.7, 1.8],
  [1.4, 1.0],
  [-1.0, -2.7],
];

/** A full trip takes this long to drive, then loops — enough to watch it move. */
const LOOP_MS = 4 * 60_000;
const TICK_MS = 3_000;

export function useLiveMapDemo(
  hubs: readonly HubMapItem[],
  isSuperAdmin: boolean,
  realStaff: readonly DemoRealStaff[] = NO_REAL_STAFF,
): LiveMapDemo | null {
  const enabled = isLiveMapDemo(isSuperAdmin);
  const [now, setNow] = useState(() => Date.now());
  const [mountedAt] = useState(() => Date.now());
  useEffect(() => {
    if (!enabled) return;
    const timer = window.setInterval(() => setNow(Date.now()), TICK_MS);
    return () => window.clearInterval(timer);
  }, [enabled]);

  const hub = hubs[0];
  const hubId = hub?.id ?? id(900);
  const origin = hub?.point ?? CAINTA;

  // Fixed places, relative to the hub.
  const places = useMemo(
    () => ({
      ana: offset(origin, 1.8, 2.4),
      bea: offset(origin, -1.2, 3.1),
      carla: offset(origin, 2.6, -1.7),
      dina: offset(origin, -2.4, -2.2),
      ella: offset(origin, 0.3, 0.4),
      faye: offset(origin, -0.4, 0.6),
      gina: offset(origin, 3.4, 1.2),
      leo: offset(origin, 0.2, -0.3),
      marco: offset(origin, -3.1, 0.9),
      kikoClient: offset(origin, -3.6, 4.2),
    }),
    [origin.lat, origin.lng],
  );

  // Two riders actually driving, along a few waypoints each.
  const junPath = useMemo(
    () => [origin, offset(origin, 0.9, -0.4), offset(origin, 1.7, -1.1), places.carla],
    [origin.lat, origin.lng, places],
  );
  const kikoPath = useMemo(
    () => [origin, offset(origin, -1.1, 1.3), offset(origin, -2.4, 2.9), places.kikoClient],
    [origin.lat, origin.lng, places],
  );

  return useMemo(() => {
    if (!enabled) return null;
    const t = (now % LOOP_MS) / LOOP_MS;
    const jun = along(junPath, t);
    const kiko = along(kikoPath, (t + 0.35) % 1);
    const fresh = new Date(now).toISOString();
    const stale = minutesAgo(9, now);

    const staffRow = (
      n: number,
      name: string,
      role: "therapist" | "rider",
      point: Point,
      ops: StaffOpsStatusWire,
    ): ClockedInStaffMapItem => ({
      id: id(n),
      name,
      role,
      hubId,
      capturedAt: ops.connected ? fresh : stale,
      locationState: "live",
      mapStatus: null,
      opsStatus: ops.connected ? ops : { ...ops, lastContactAt: stale },
      point,
    });

    const b = (n: number) => id(500 + n);
    const staff: ClockedInStaffMapItem[] = [
      staffRow(
        1,
        "Ana R.",
        "therapist",
        places.ana,
        status({
          activity: "on_going_service",
          label: "On Going Service",
          since: minutesAgo(40, now),
          bookingId: b(1),
          bookingRef: "DEMO-1001",
        }),
      ),
      staffRow(
        2,
        "Bea S.",
        "therapist",
        places.bea,
        status({
          activity: "at_client_waiting_to_start",
          label: "At the client – Waiting to start",
          since: minutesAgo(6, now),
          bookingId: b(2),
          bookingRef: "DEMO-1002",
        }),
      ),
      staffRow(
        3,
        "Carla M.",
        "therapist",
        places.carla,
        status({
          activity: "waiting_for_pick_up",
          label: "Waiting for Pick-Up",
          pickUpFor: "client",
          since: minutesAgo(25, now),
          bookingId: b(3),
          bookingRef: "DEMO-1003",
        }),
      ),
      staffRow(
        4,
        "Dina L.",
        "therapist",
        places.dina,
        status({
          activity: "on_going_service",
          label: "Disconnected : On Going",
          connected: false,
          since: minutesAgo(55, now),
          bookingId: b(4),
          bookingRef: "DEMO-1004",
        }),
      ),
      staffRow(
        5,
        "Ella P.",
        "therapist",
        places.ella,
        status({
          activity: "active",
          label: "Active",
          since: minutesAgo(95, now),
        }),
      ),
      staffRow(
        6,
        "Faye G.",
        "therapist",
        places.faye,
        status({
          activity: "on_break",
          label: "On Break",
          pauseKind: "regular",
          since: minutesAgo(12, now),
        }),
      ),
      staffRow(
        7,
        "Gina T.",
        "therapist",
        places.gina,
        status({
          activity: "collecting_payment",
          label: "Collecting Payment",
          since: minutesAgo(3, now),
          bookingId: b(5),
          bookingRef: "DEMO-1005",
        }),
      ),
      staffRow(
        8,
        "Hana Q.",
        "therapist",
        kiko,
        status({
          activity: "enroute",
          label: "Enroute",
          since: minutesAgo(9, now),
          bookingId: b(6),
          bookingRef: "DEMO-1006",
        }),
      ),
      staffRow(
        11,
        "Jun C.",
        "rider",
        jun,
        status({
          activity: "pick_up_en_route",
          label: "Pick Up – En route",
          since: minutesAgo(7, now),
          bookingId: b(3),
          bookingRef: "DEMO-1003",
        }),
      ),
      staffRow(
        12,
        "Kiko D.",
        "rider",
        kiko,
        status({
          activity: "drop_off_en_route",
          label: "Drop Off – En route",
          since: minutesAgo(9, now),
          bookingId: b(6),
          bookingRef: "DEMO-1006",
        }),
      ),
      staffRow(
        13,
        "Leo B.",
        "rider",
        places.leo,
        status({
          activity: "active",
          label: "Active",
          since: minutesAgo(20, now),
        }),
      ),
      staffRow(
        14,
        "Marco V.",
        "rider",
        places.marco,
        status({
          activity: "return_en_route",
          label: "Disconnected : Return – En route",
          connected: false,
          since: minutesAgo(14, now),
        }),
      ),
      staffRow(
        15,
        "Nico A.",
        "rider",
        offset(origin, 0.1, 0.1),
        status({
          activity: "not_in_session",
          label: "Not in session",
        }),
      ),
    ];

    const trips: OpsLiveTrip[] = [
      {
        tripId: id(701),
        bookingRef: "DEMO-1003",
        hubId,
        riderId: id(11),
        riderName: "Jun C.",
        therapistId: id(3),
        status: "dispatched",
        leg: "to_pickup",
        pos: jun,
        recordedAt: fresh,
        live: true,
      },
      {
        tripId: id(702),
        bookingRef: "DEMO-1006",
        hubId,
        riderId: id(12),
        riderName: "Kiko D.",
        therapistId: id(8),
        status: "en_route_to_client",
        leg: "to_client",
        pos: kiko,
        recordedAt: fresh,
        live: true,
      },
      {
        tripId: id(703),
        bookingRef: "DEMO-1001",
        hubId,
        riderId: id(13),
        riderName: "Leo B.",
        therapistId: id(1),
        status: "in_session",
        leg: "waiting",
        pos: places.ana,
        recordedAt: minutesAgo(41, now),
        live: false,
      },
    ];

    const variant = (name: string, minutes: number) => ({
      duration_minutes: minutes,
      services: { name },
    });
    const booking = (
      n: number,
      ref: string,
      bookingStatus: string,
      scheduledMinutesFromNow: number,
      therapist: number | null,
      rider: number | null,
      service: [string, number],
    ) =>
      ({
        id: b(n),
        ref,
        status: bookingStatus,
        scheduled_at: new Date(now + scheduledMinutesFromNow * 60_000).toISOString(),
        hub_id: hubId,
        therapist_id: therapist ? id(therapist) : null,
        rider_id: rider ? id(rider) : null,
        booking_group_id: null,
        service_variants: variant(...service),
      }) as unknown as LiveMapBooking;

    const bookings: LiveMapBooking[] = [
      booking(1, "DEMO-1001", "in_session", -45, 1, 13, ["Swedish Massage", 90]),
      booking(2, "DEMO-1002", "arrived", -5, 2, 11, ["Signature Massage", 60]),
      booking(3, "DEMO-1003", "dispatched", 20, 3, 11, ["Hot Stone Massage", 90]),
      booking(4, "DEMO-1004", "in_session", -60, 4, 13, ["Deep Tissue Massage", 120]),
      booking(5, "DEMO-1005", "completed", -95, 7, 12, ["Swedish Massage", 60]),
      booking(6, "DEMO-1006", "en_route_to_client", 10, 8, 12, ["Couple Massage Package", 90]),
      booking(7, "DEMO-1007", "awaiting_rider", -45, 5, null, ["Foot Reflexology", 60]),
      booking(8, "DEMO-1008", "awaiting_rider", 75, 6, null, ["Kiddie Massage", 30]),
      booking(9, "DEMO-1009", "pending", 180, null, null, ["Pregnancy Massage", 60]),
      // Arrives ~20 s after the page opens, so the live alert stack has
      // something NEW to announce — what was there on load is never news.
      ...(now - mountedAt > 20_000
        ? [booking(11, "DEMO-1011", "dispatched", -35, 5, 13, ["Swedish Massage", 60])]
        : []),
    ];

    const faces = realStaff.slice(0, REAL_STAFF_SPOTS.length).map((person, index) => {
      const [north, east] = REAL_STAFF_SPOTS[index] ?? [0, 0];
      return {
        ...staffRow(
          900 + index,
          person.name,
          person.role,
          offset(origin, north, east),
          status({ activity: "active", label: "Active", since: minutesAgo(20, now) }),
        ),
        id: person.id,
      };
    });

    return {
      staff: [...staff, ...faces],
      trips,
      bookings,
      noShows: [
        {
          id: id(801),
          party: "client",
          created_at: minutesAgo(18, now),
          booking_id: b(10),
          bookings: { ref: "DEMO-0998", hub_id: hubId },
        },
      ],
      pendingCash: [
        {
          id: id(851),
          amount: 1250,
          status: "pending",
          hub_id: hubId,
          created_at: minutesAgo(70, now),
        },
        {
          id: id(852),
          amount: 899,
          status: "pending",
          hub_id: hubId,
          created_at: minutesAgo(30, now),
        },
      ],
      trackFor: (tripId) => {
        if (tripId === id(701)) return trackUntil(junPath, t, now);
        if (tripId === id(702)) return trackUntil(kikoPath, (t + 0.35) % 1, now);
        return undefined;
      },
    };
  }, [
    enabled,
    now,
    mountedAt,
    hubId,
    origin.lat,
    origin.lng,
    places,
    junPath,
    kikoPath,
    realStaff,
  ]);
}

/** The driven line so far, sampled along the path up to `t`. */
function trackUntil(path: readonly Point[], t: number, now: number): OpsLiveTripTrack {
  const steps = Math.max(2, Math.round(t * 40));
  return Array.from({ length: steps }, (_, i) => {
    const point = along(path, (t * i) / (steps - 1));
    return { ...point, recordedAt: new Date(now - (steps - i) * 5_000).toISOString() };
  });
}
